import { atom, selector } from "recoil"

import type { Proposal, ProposalIncentive } from "~/graphql/client"
import { SiteKind, StateCode } from "~/graphql/client"
import { costOfLineItem } from "~/utils/finance/pricing-summary"
import { selectDiff } from "~/utils/recoil"

import { ProposalLanguage } from "./proposal-language"
import type { BarData } from "../../Brochure/SavingsOverTimeSlide/bar-data-type"
import { DEFAULT_USER } from "../user/constants"
import { DEFAULT_UTILITY_RATE } from "../utility-rate/utility-rate-state"

export const isProposal = (maybeProposal: unknown): maybeProposal is Proposal =>
  typeof maybeProposal === `object` &&
  (maybeProposal as { __typename: string })?.__typename === `Proposal`

// [eyecue-codemap-group:FtCrg1Femx]
export const DEFAULT_PROPOSAL: Proposal = {
  enterpriseSettings: null,
  proposalAdders: [],
  annualEnergyUse: null,
  avgMonthlyBill: null,
  seller: DEFAULT_USER,
  sellerId: ``,
  primaryPresenter: 0,
  secondaryPresenter: 0,
  createdAt: ``,
  currentStatus: ``,
  displayAddressId: ``,
  displayAddress: {
    id: ``,
    createdAt: ``,
    updatedAt: ``,
    category: ``,
    city: ``,
    lat: 0,
    long: 0,
    postal: ``,
    projectId: 0,
    state: ``,
    street: ``,
  },
  enterpriseLogoUrl: null,
  homeowner: {
    id: ``,
    createdAt: ``,
    updatedAt: ``,
    powurProjectId: 0,
    firstName: ``,
    lastName: ``,
    email: ``,
    phone: null,
  },
  id: ``,
  margin: 0.3,
  powurProjectId: 0,
  monthlyEnergyCost: Array(12).fill(null),
  monthlyEnergyManualFlags: Array(12).fill(false),
  monthlyEnergyUse: Array(12).fill(null),
  smartEstimateBanner: null,
  genabilityDefaultContractedRate: null,
  genabilityUsageEstimate: null,
  usageProfile: null,
  usageProfileId: null,
  shortLink: null,
  postSolarUtilityRate: DEFAULT_UTILITY_RATE,
  postSolarUtilityRateId: ``,
  designs: [],
  activeLoans: [],
  downPayment: 0,
  selectedLoanProduct: null,
  selectedLoanProductId: null,
  siteId: ``,
  slug: ``,
  systemType: null,
  secondarySystem: false,
  newConstruction: false,
  updatedAt: ``,
  utilityRate: DEFAULT_UTILITY_RATE,
  utilityRateId: ``,
  utilityRateEscalationPercentage: 0,
  enterprise: null,
  enterpriseId: ``,
  mentor: DEFAULT_USER,
  mentorId: ``,
  quote: null,
  quotes: [],
  incentives: [],
  proposalCost: {
    id: ``,
    createdAt: ``,
    updatedAt: ``,
    totalCost: 0,
    salesTax: 0,
    exciseTax: 0,
    designCost: 0,
    batteryCost: 0,
    lineItems: [],
    proposalId: ``,
  },
  site: {
    id: ``,
    address: ``,
    isEnergyCommunity: false,
    isEnergyCommunityManuallySetAt: null,
    kind: SiteKind.solar,
    latitude: 0,
    longitude: 0,
    streetViewLatitude: 0,
    streetViewLongitude: 0,
    createdAt: ``,
    updatedAt: ``,
    imagery: [],
    state: StateCode.PR,
    street: ``,
    locality: ``,
    postalCode: ``,
    postalCodeSuffix: ``,
    genabilityAccountId: null,
  },
  newCustomerIncentive: false,
  preferredLanguage: ProposalLanguage.English,
  proposalStatuses: [],
  requestType: ``,
  metadata: ``,
  userAdjustedAfterSolarContractedRate: null,
  userAdjustedBeforeSolarContractedRate: null,
  userAdjustedUtilityRate: null,
  name: null,
  allowChangeOrderEquipment: false,
  requiresRefresh: false,
  financeType: null,
  financeProductId: null,
  leadSource: null,
  fundingGroup: null,
}
// [end-eyecue-codemap-group:FtCrg1Femx:044007818ab3b2518d336b191047a2e819130092]

export const proposalLocalState = atom<Proposal>({
  key: `proposalLocal`,
  default: DEFAULT_PROPOSAL,
})

export const proposalRemoteState = atom<Proposal>({
  key: `proposalRemote`,
  default: DEFAULT_PROPOSAL,
})

export const proposalDiffState = selectDiff(
  proposalLocalState,
  proposalRemoteState,
)

export const annualUsageState = selector({
  key: `annualUsage`,
  get: ({ get }) => get(proposalRemoteState).annualEnergyUse ?? 0,
})

export const monthlyEnergyUsageBarDataState = selector<BarData>({
  key: `monthlyEnergyUsageLineData`,
  get: ({ get }) =>
    get(proposalRemoteState).monthlyEnergyUse.map((e, i) => ({
      y: e ?? 0,
      x: new Date(new Date().getFullYear(), i),
    })),
})

export const byFederalThenValue =
  () =>
  (a: ProposalIncentive, b: ProposalIncentive): number => {
    if (b.incentive.jurisdiction === `federal`) {
      return 1
    }

    return b.value - a.value
  }

export const incentivesFinancingBreakdownState = selector<ProposalIncentive[]>({
  key: `incentivesFinancingBreakdownState`,
  get: ({ get }) => {
    const proposal = get(proposalLocalState)

    return (
      proposal.incentives
        ?.filter((i) => {
          const isShowOnBrochure = i.showOnBrochure
          return isShowOnBrochure
        })
        .sort(byFederalThenValue()) ?? []
    )
  },
})

export const newCustomerIncentiveState = selector<number>({
  key: `newCustomerIncentive`,
  get: ({ get }) => {
    const proposal = get(proposalRemoteState)

    if (!proposal.newCustomerIncentive) {
      return 0
    }

    const item = proposal.proposalCost.lineItems.find(
      (li) => li.displayName === `New Customer Incentive`,
    )

    if (!item) {
      return 0
    }

    return costOfLineItem(item)
  },
})
